import { useEffect, useMemo, useState } from 'react';
import { useApiGateway } from 'hooks/useApi';
import { CALENDAR_DATA_BY_DATE } from 'constants/stockApi';
import { getIsNativePlatform } from 'utils/client/mobile';
import { getCurrentETDateString } from 'screens/Calendar/utils/dateUtils';

/**
 * Days on each side of today that the native app reloads when a calendar opens: the same ± 10 days
 * the build bakes in (`getUnified*Calendar`), so the app covers exactly what the web shows. The
 * preset views reach further than their names say: "Last 7 Days" starts 8 days back, or 9 in a
 * daylight-saving change week (see `getDateRangeForTimeframe`). BaseCalendar still fetches any
 * other date on demand when the user picks it.
 */
export const NATIVE_CALENDAR_WINDOW_DAYS = 10;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface CalendarDay {
  date: string;
  items: any[];
  lastUpdated?: string;
}

/** The `/v1/products/calendars/{slug}/{date}` route names the economic-events calendar `economic`. */
export const getCalendarApiSlug = (slug?: string) =>
  slug === 'economic-events' ? 'economic' : slug;

/** `YYYY-MM-DD` dates from `days` before to `days` after the given ET date, oldest first. */
export const getCalendarWindowDates = (
  todayET: string,
  days: number = NATIVE_CALENDAR_WINDOW_DAYS,
): string[] => {
  const today = Date.parse(`${todayET}T00:00:00.000Z`);

  return Array.from({ length: days * 2 + 1 }, (_, index) =>
    new Date(today + (index - days) * DAY_MS).toISOString().slice(0, 10),
  );
};

/**
 * Replaces the days `fresh` covers, keeps the rest of `base` in place, and appends the days `base`
 * lacks, oldest first.
 */
export const mergeCalendarDays = (base: CalendarDay[], fresh: CalendarDay[]): CalendarDay[] => {
  const freshByDate = new Map(fresh.map((day) => [day.date, day]));
  const merged = base.map((day) => freshByDate.get(day?.date) ?? day);
  const baseDates = new Set(base.map((day) => day?.date));

  const added = fresh.filter((day) => !baseDates.has(day.date));

  return [...merged, ...added.sort((a, b) => a.date.localeCompare(b.date))];
};

/**
 * The calendar data a page should show.
 *
 * The native app is a static export, so `serverData` is whatever `getStaticProps` fetched on the
 * day the app was built (build date ± 10 days) and ISR never runs. A build older than 10 days has
 * no rows for "Today" at all. On native, this reloads the current window from the per-date API
 * when the page opens, and shows the build-time copy only for days that fail to load. On the web,
 * ISR keeps `serverData` current, so it is returned unchanged.
 */
export function useNativeCalendarData(slug: string, serverData: CalendarDay[]) {
  // Tagged with its calendar: tab changes keep this page mounted, and one calendar's days must
  // never render under another's tab.
  const [fresh, setFresh] = useState<{ slug: string; days: CalendarDay[] } | null>(null);
  // Starts true on native so the empty state never flashes before the first request goes out.
  const [isRefreshing, setIsRefreshing] = useState(() => getIsNativePlatform());
  const { get } = useApiGateway<void, CalendarDay>(
    `${CALENDAR_DATA_BY_DATE}/${getCalendarApiSlug(slug)}`,
  );

  useEffect(() => {
    if (!getIsNativePlatform()) return;

    let isActive = true;
    const dates = getCalendarWindowDates(getCurrentETDateString());

    setFresh({ slug, days: [] });
    setIsRefreshing(true);

    // Each day shows as soon as it arrives, so one slow day never holds back Today.
    const requests = dates.map((date) =>
      get({ endpoint: `/${date}` }).then((result) => {
        const day = result.data;
        if (!isActive || result.isError || !day || typeof day !== 'object') return;

        const loaded = { date, items: day.items ?? [], lastUpdated: day.lastUpdated };
        setFresh((current) => ({
          slug,
          days: [...(current?.slug === slug ? current.days : []), loaded],
        }));
      }),
    );

    Promise.all(requests).then(() => {
      if (isActive) setIsRefreshing(false);
    });

    return () => {
      isActive = false;
    };
    // `get` is a new function on every render; the request only depends on the calendar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const data = useMemo(
    () => (fresh?.slug === slug ? mergeCalendarDays(serverData, fresh.days) : serverData),
    [serverData, fresh, slug],
  );

  return { data, isRefreshing };
}
